import { type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { store, persistor } from '@doccareplus/kernel/application';
import { ThemeProvider } from '@doccareplus/shared/lib/theme';
import { ErrorBoundary, GlobalLoader, ServerWakeGate } from '@doccareplus/ui';
import { AuthSessionBridge } from './AuthSessionBridge';
import { PwaUpdateContainer } from './PwaUpdateContainer';
import { ToastHost } from './ToastHost';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      // The API client owns retries for explicitly transient statuses. Avoid a
      // second retry layer here, especially for server-side 500 responses.
      retry: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * App Providers - Wraps the entire app with necessary providers
 * Order matters: outermost providers are listed first
 * 
 * Provider hierarchy:
 * 1. Redux Provider - Global state management
 * 2. PersistGate - Delays rendering until persisted state is retrieved
 * 3. QueryClientProvider - Server state management (TanStack Query)
 * 4. BrowserRouter - Client-side routing
 * 5. ThemeProvider - Theme management
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <Provider store={store}>
      <PersistGate loading={<GlobalLoader />} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ThemeProvider defaultTheme="light" storageKey="app-theme">
              <AuthSessionBridge />
              <ServerWakeGate />
              <ErrorBoundary>{children}</ErrorBoundary>
              <ToastHost />
              <PwaUpdateContainer />
            </ThemeProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}
